interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

// Construit la liste des pages à afficher avec des ellipses quand il y a
// beaucoup de pages : 1 … 4 5 [6] 7 8 … 20
function buildPageItems(current: number, total: number): (number | 'gap')[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const items: (number | 'gap')[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  if (start > 2) items.push('gap');
  for (let i = start; i <= end; i += 1) items.push(i);
  if (end < total - 1) items.push('gap');

  items.push(total);
  return items;
}

// Pagination numérotée réutilisable : précédent / pages / suivant.
export default function Pagination({ currentPage, totalPages, onPageChange }: PaginationProps) {
  if (totalPages <= 1) return null;

  const pages = buildPageItems(currentPage, totalPages);

  const baseBtn =
    'inline-flex items-center justify-center min-w-9 h-9 px-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap';

  return (
    <nav aria-label="Pagination" className="flex items-center justify-center gap-1.5 flex-wrap mt-8">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label="Page précédente"
        className={`${baseBtn} border border-background-300 text-foreground-800 hover:border-primary-400 ${
          currentPage === 1 ? 'opacity-40 cursor-not-allowed' : ''
        }`}
      >
        <i className="ri-arrow-left-s-line w-5 h-5 flex items-center justify-center" />
      </button>

      {pages.map((item, index) =>
        item === 'gap' ? (
          <span
            key={`gap-${index}`}
            className="inline-flex items-center justify-center min-w-9 h-9 text-foreground-400 text-sm select-none"
            aria-hidden="true"
          >
            …
          </span>
        ) : (
          <button
            key={item}
            onClick={() => onPageChange(item)}
            aria-label={`Page ${item}`}
            aria-current={item === currentPage ? 'page' : undefined}
            className={`${baseBtn} ${
              item === currentPage
                ? 'bg-primary-500 text-background-50'
                : 'border border-background-300 text-foreground-800 hover:border-primary-400'
            }`}
          >
            {item}
          </button>
        ),
      )}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label="Page suivante"
        className={`${baseBtn} border border-background-300 text-foreground-800 hover:border-primary-400 ${
          currentPage === totalPages ? 'opacity-40 cursor-not-allowed' : ''
        }`}
      >
        <i className="ri-arrow-right-s-line w-5 h-5 flex items-center justify-center" />
      </button>
    </nav>
  );
}